import React, { useEffect, useRef, useState } from 'react';

/**
 * Ultra-Gerçekçi Şimşek & Elektrik Plazma Efekti
 * Yalnızca hero ekranda görünürken çalışır (IntersectionObserver ile performans optimizasyonlu).
 * Düz çizgiler yerine gerçekçi dallanan (dendritik) SVG vektör yolları ve çok katmanlı Gaussian Glow içerir.
 */
const LightningBackdrop = () => {
  const ref = useRef(null);
  const [inView, setInView] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;

    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.05, rootMargin: '40px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`hero-lightning-layer${inView ? '' : ' effects-paused'}`}
      aria-hidden="true"
    >
      {/* Atmosferik Plazma Flaş Katmanları (Şimşek çaktığında gökyüzü parlar) */}
      <div className="hero-electric-flash flash-left" />
      <div className="hero-electric-flash flash-right" />
      <div className="hero-electric-flash flash-sheet" />

      {/* Yüksek Çözünürlüklü Vektörel Şimşek Ağları (SVG) */}
      <svg
        className="realistic-lightning-svg"
        viewBox="0 0 1440 900"
        preserveAspectRatio="none"
        style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }}
      >
        <defs>
          {/* Kırmızı / Pembe Neon Plazma Glow */}
          <filter id="lightning-glow-pink" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="blur1" />
            <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="blur2" />
            <feGaussianBlur in="SourceGraphic" stdDeviation="24" result="blur3" />
            <feMerge>
              <feMergeNode in="blur3" />
              <feMergeNode in="blur2" />
              <feMergeNode in="blur1" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Mavi / Camgöbeği Elektrik Glow */}
          <filter id="lightning-glow-cyan" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="blur1" />
            <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="blur2" />
            <feGaussianBlur in="SourceGraphic" stdDeviation="24" result="blur3" />
            <feMerge>
              <feMergeNode in="blur3" />
              <feMergeNode in="blur2" />
              <feMergeNode in="blur1" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Mor / Altın Yıldırım Glow */}
          <filter id="lightning-glow-purple" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur1" />
            <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="blur2" />
            <feMerge>
              <feMergeNode in="blur2" />
              <feMergeNode in="blur1" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* ─── Şimşek 1: Sol Üstten Merkeze (Kırmızı/Pembe Plazma Çekirdekli) ─── */}
        <g className="realistic-bolt realistic-bolt-1" filter="url(#lightning-glow-pink)">
          {/* Dış Hale (Plasma Aura) */}
          <path
            d="M 280,-10 L 265,110 L 295,135 L 245,260 L 275,290 L 220,440 L 255,475 L 195,640"
            fill="none"
            stroke="#ff3366"
            strokeWidth="7"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.65"
          />
          {/* İç Beyaz Sıcak Çekirdek (White-Hot Core) */}
          <path
            d="M 280,-10 L 265,110 L 295,135 L 245,260 L 275,290 L 220,440 L 255,475 L 195,640"
            fill="none"
            stroke="#ffffff"
            strokeWidth="2.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Yan Kollar (Dendritic Forks) */}
          <path
            d="M 245,260 L 190,320 L 175,410 M 220,440 L 275,510 L 305,600 M 265,110 L 315,160 L 330,220"
            fill="none"
            stroke="#ffb3c1"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>

        {/* ─── Şimşek 2: Sağ Üstten Sağa Doğru Çift Çatal (Cyan/Mavi Elektrik) ─── */}
        <g className="realistic-bolt realistic-bolt-2" filter="url(#lightning-glow-cyan)">
          {/* Dış Hale */}
          <path
            d="M 1150,-20 L 1120,130 L 1155,160 L 1090,310 L 1130,340 L 1065,510 L 1100,545 L 1030,710"
            fill="none"
            stroke="#00e1ff"
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.6"
          />
          {/* Beyaz Çekirdek */}
          <path
            d="M 1150,-20 L 1120,130 L 1155,160 L 1090,310 L 1130,340 L 1065,510 L 1100,545 L 1030,710"
            fill="none"
            stroke="#ffffff"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Çatallı Kollar */}
          <path
            d="M 1090,310 L 1160,380 L 1195,490 M 1065,510 L 1005,580 L 980,680 M 1120,130 L 1060,180"
            fill="none"
            stroke="#a5f3fc"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>

        {/* ─── Şimşek 3: Merkez Uzak Yıldırım (Mor/Altın Yüksek Voltaj) ─── */}
        <g className="realistic-bolt realistic-bolt-3" filter="url(#lightning-glow-purple)">
          <path
            d="M 720,0 L 735,95 L 710,120 L 745,230 L 715,260 L 740,390"
            fill="none"
            stroke="#c084fc"
            strokeWidth="5"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.5"
          />
          <path
            d="M 720,0 L 735,95 L 710,120 L 745,230 L 715,260 L 740,390"
            fill="none"
            stroke="#ffffff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M 745,230 L 785,290 L 805,370"
            fill="none"
            stroke="#e9d5ff"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        </g>
      </svg>
    </div>
  );
};

export default LightningBackdrop;
